import React, { useMemo } from 'react';
import targets from './salesDataTargets.json';
import Link from './Link';
import StatusPill, { getStatusColors } from './StatusPill';
import { MONTHS } from './constants';
import { fmt, fmtPercent } from './format';
import { buildResults, getDrillInvoices, getLastStartedMonth, sum } from './salesCalculations';
import {
    amountStyle, cardStyle, cellStyle, footRowStyle, groupStart, headCenterStyle, headRowStyle, headStyle, rowHeadStyle, stripeStyle, tableStyle,
} from './styles';

const SalesTable = ({ buckets, unplacedCount, onDrill }) => {
    const results = useMemo(() => buildResults(buckets), [buckets]);

    const lastMonth = getLastStartedMonth();

    const totalLastYear = MONTHS.map((_, m) => sum(targets.salespersons.map((p) => p.lastYear[m])));
    const totalBudget = MONTHS.map((_, m) => sum(targets.salespersons.map((p) => p.budget[m])));
    const totalResult = MONTHS.map((_, m) => sum(results.map((r) => r[m])));

    // Accumulated result / accumulated budget up to and including the month
    const statusFor = (month) => sum(totalResult.slice(0, month + 1)) / sum(totalBudget.slice(0, month + 1));

    // Result value rendered as a link to the invoices behind it, colored by how it compares to the budget (ratio)
    const resultCell = (value, personIndex, month, ratio) => {
        const selection = { personIndex, month };
        const hasInvoices = getDrillInvoices(buckets, selection).length > 0
            || (personIndex === null && month === null && unplacedCount > 0);
        const colors = Number.isFinite(ratio) ? getStatusColors(ratio) : {};
        return (
            <td style={{ ...amountStyle(value), ...colors }}>
                {hasInvoices
                    ? <Link onClick={() => onDrill(selection)} style={{ ...amountStyle(value, {}), ...(colors.color && { color: colors.color }) }}>{fmt(value)}</Link>
                    : fmt(value)}
            </td>
        );
    };

    // Result / budget up to and including the last started month, so a year in progress is not judged against the whole budget
    const ratioToDate = (resultByMonth, budgetByMonth) =>
        sum(resultByMonth.slice(0, lastMonth + 1)) / sum(budgetByMonth.slice(0, lastMonth + 1));

    return (
        <div style={cardStyle}>
            <table style={tableStyle}>
                <thead>
                    <tr style={headRowStyle}>
                        <th />
                        {targets.salespersons.map((p) => (
                            <th key={p.name} colSpan={3} style={{ ...headCenterStyle, ...groupStart }}>{p.name}</th>
                        ))}
                        <th colSpan={3} style={{ ...headCenterStyle, ...groupStart }}>Totalt</th>
                        <th rowSpan={2} style={{ ...headCenterStyle, ...groupStart, verticalAlign: 'bottom' }}>Total fsg / budget</th>
                    </tr>
                    <tr style={headRowStyle}>
                        <th />
                        {[...targets.salespersons, null].map((p) => (
                            <React.Fragment key={p ? p.name : 'total'}>
                                <th style={{ ...headStyle, ...groupStart }}>{targets.year - 1}</th>
                                <th style={headStyle}>Budget</th>
                                <th style={headStyle}>Resultat</th>
                            </React.Fragment>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {MONTHS.map((month, m) => {
                        const started = m <= lastMonth;
                        return (
                            <tr key={month} style={stripeStyle(m)}>
                                <td style={rowHeadStyle}>{month}</td>
                                {targets.salespersons.map((p, i) => (
                                    <React.Fragment key={p.name}>
                                        <td style={{ ...cellStyle, ...groupStart }}>{fmt(p.lastYear[m])}</td>
                                        <td style={cellStyle}>{fmt(p.budget[m])}</td>
                                        {started ? resultCell(results[i][m], i, m, results[i][m] / p.budget[m]) : <td style={cellStyle} />}
                                    </React.Fragment>
                                ))}
                                <td style={{ ...cellStyle, ...groupStart }}>{fmt(totalLastYear[m])}</td>
                                <td style={cellStyle}>{fmt(totalBudget[m])}</td>
                                {started ? resultCell(totalResult[m], null, m, totalResult[m] / totalBudget[m]) : <td style={cellStyle} />}
                                <td style={{ ...cellStyle, ...groupStart, textAlign: 'center' }}>
                                    {started && <StatusPill ratio={statusFor(m)}>{fmtPercent(statusFor(m))}</StatusPill>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr style={footRowStyle}>
                        <td style={rowHeadStyle}>Tot</td>
                        {targets.salespersons.map((p, i) => (
                            <React.Fragment key={p.name}>
                                <td style={{ ...cellStyle, ...groupStart }}>{fmt(sum(p.lastYear))}</td>
                                <td style={cellStyle}>{fmt(sum(p.budget))}</td>
                                {resultCell(sum(results[i]), i, null, ratioToDate(results[i], p.budget))}
                            </React.Fragment>
                        ))}
                        <td style={{ ...cellStyle, ...groupStart }}>{fmt(sum(totalLastYear))}</td>
                        <td style={cellStyle}>{fmt(sum(totalBudget))}</td>
                        {resultCell(sum(totalResult), null, null, ratioToDate(totalResult, totalBudget))}
                        <td style={groupStart} />
                    </tr>
                    <tr style={{ backgroundColor: '#f6f7f9', fontWeight: 'bold' }}>
                        <td style={rowHeadStyle}>%</td>
                        {targets.salespersons.map((p, i) => (
                            <React.Fragment key={p.name}>
                                <td style={groupStart} />
                                <td />
                                <td style={{ ...cellStyle, ...getStatusColors(ratioToDate(results[i], p.budget)) }}>{fmtPercent(sum(results[i]) / sum(p.budget))}</td>
                            </React.Fragment>
                        ))}
                        <td style={groupStart} />
                        <td />
                        <td style={{ ...cellStyle, ...getStatusColors(ratioToDate(totalResult, totalBudget)) }}>{fmtPercent(sum(totalResult) / sum(totalBudget))}</td>
                        <td style={groupStart} />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

export default SalesTable;
